import { createFileRoute } from "@tanstack/react-router";

import { AlertWarning } from "@kuzco/web/ui";

import type { IntegrationStepConfig } from "../../../components/integrations/IntegrationPageShell";
import { ApiKeyPreviewField } from "../../../components/integrations/ApiKeyPreviewField";
import { CopyField } from "../../../components/integrations/CopyField";
import { IntegrationPageShell } from "../../../components/integrations/IntegrationPageShell";
import { useNubankCredentials } from "../../../components/integrations/useNubankCredentials";

export const Route = createFileRoute("/_authed/integrations/jetbrains")({
  component: JetbrainsIntegrationPage,
  head: () => ({
    meta: [{ title: "JetBrains | NBDC" }],
  }),
});

function JetbrainsIntegrationPage() {
  const { keyPreview, baseUrl, isPending, errorMessage } =
    useNubankCredentials();
  const openAiBaseUrl = `${baseUrl}/v1`;

  const steps: IntegrationStepConfig[] = [
    {
      title: "Open Providers & API Keys",
      description:
        "In JetBrains, open Settings and navigate to Tools > AI Assistant > Providers & API Keys.",
      content: null,
    },
    {
      title: "Go to Third-party AI Providers",
      description: "Switch to the Third-party AI providers tab.",
      content: null,
    },
    {
      title: "Configure OpenAI-compatible Provider",
      description:
        'Select "OpenAI-compatible", paste the URL below, and paste your saved API key.',
      content: (
        <div className="flex flex-col gap-4">
          <CopyField label="URL" value={openAiBaseUrl} />
          <ApiKeyPreviewField
            value={keyPreview}
            isLoading={isPending}
            errorMessage={errorMessage}
          />
        </div>
      ),
    },
    {
      title: "Enable Tool Calling (Optional)",
      description:
        'Recommended for optimal use. In the provider settings, toggle "Tool calling" on so the assistant can use tools.',
      content: null,
    },
    {
      title: "Set Context Window to 1,000,000 (Optional)",
      description:
        'Recommended for optimal use. Under "Model Assignment", set the Context window to 1000000 to take full advantage of the model.',
      content: <CopyField label="Context window" value="1000000" />,
    },
  ];

  return (
    <IntegrationPageShell
      title="JetBrains"
      badge="alpha"
      description="Use the custom Nubank model from JetBrains AI Assistant."
      intro={
        <AlertWarning
          className="bg-detail-warning/10"
          title="Alpha integration"
          content="This integration isn't as well tested as the others and is harder to set up, but it does work."
        />
      }
      steps={steps}
    />
  );
}
